import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService } from '@bp-core/src/lib/services/portal/auth.service';
import { UserRolesService } from '@bp-core/src/lib/services/portal/user-roles.service';
import { SudoAccessDeferral, landingUrl } from 'bp/shared/guards/sudo-access.guard';
import { SudoAccessStore } from 'bp/shared/services/sudo-access.store';

import { OAuthService } from 'projects/bp-core/src/lib/external/angular-oauth2-oidc-jwks/oauth-service';
import { filter, take } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthGuard {
  private readonly _rolesService = inject(UserRolesService);
  private readonly _deferral = inject(SudoAccessDeferral);
  private readonly _sudoAccess = inject(SudoAccessStore);

  constructor(
    public authService: AuthService,
    private router: Router,
    private oauthService: OAuthService,
  ) {}

  /**
   * Decide si el usuario autenticado puede acceder a una ruta protegida.
   * - admin / sudo → permitido (comportamiento legado).
   * - Con acceso al sudo pero sin admin/sudo (rol de área, customer-success, ia-manager) → redirigir a su
   *   inicio (`homeFor`), para que no acceda a /dashboard, /users ni otros módulos no autorizados.
   * - Resto → redirigir a /errors/401.
   *
   * Si `sudoAccessGuard` dejó pasar esta navegación porque los permisos aún no llegaban (carga directa de
   * una URL), este guard no decide con los roles del token — no conoce los roles de área como `marketing` y
   * los mandaría a la 401—: cede el paso y el layout revalida la URL con los permisos (feature 057, H4).
   */
  private resolveByRole(resolve: (value: boolean) => void, deferred: boolean): void {
    if (this._rolesService.isAdmin() || this._rolesService.isSudo() || deferred) {
      resolve(true);
      return;
    }
    // A su inicio si tiene acceso al sudo; si no, a la 401 (BT-7551).
    this.router.navigateByUrl(landingUrl(this._sudoAccess));
    resolve(false);
  }

  canActivate(): Promise<boolean> {
    // Se lee ahora: la navegación en curso ya no se consulta después de los `await` de abajo.
    const deferred = this._deferral.isDeferred();
    return new Promise((resolve, reject) => {
      this.authService.hasDiscoveredSubject$
        .pipe(
          filter(e => e === true),
          take(1),
        )
        .subscribe(async () => {
          if (this.oauthService.hasValidIdToken() || this.oauthService.hasValidAccessToken()) {
            try {
              await this.oauthService.refreshToken();

              //  this.oauthService.checkSession();
              return this.authService.userSubject$.pipe(take(1)).subscribe(user => {
                console.log('user', user);
                console.log('admin', user?.isAdmin);
                if (user && user.isAdmin) {
                  return resolve(true);
                } else if (!user) {
                  this.authService.refreshUser().subscribe(
                    data => {
                      console.log('user', data);
                      console.log('admin', data?.isAdmin);
                      if (data && data.isAdmin) {
                        return resolve(true);
                      } else {
                        // Puede ser un usuario ia-manager (no admin). Evaluar rol antes de 401.
                        this.resolveByRole(resolve, deferred);
                      }
                    },
                    error => {
                      console.error(error);
                      return resolve(false);
                    },
                  );
                } else {
                  // Usuario presente pero no admin: evaluar si es ia-manager.
                  this.resolveByRole(resolve, deferred);
                }
              });
            } catch (error) {
              console.error(error);
              this.oauthService.initCodeFlow(window.location.href);
              return resolve(false);
            }
          } else {
            console.log(
              'canActivate',

              this.oauthService.hasValidIdToken(),
              this.oauthService.hasValidAccessToken(),
            );

            this.oauthService.initCodeFlow(window.location.href);
            return resolve(false);
          }
        });
    });
  }
}
